// Find Path progress (Amy 2026-09-28: "find path should have a loading icon
// or something to indicate that it is working", and the "Finding path
// between..." line was unstyled).
//
// neuroglancer's graphServer.findPath (graphene/frontend.ts) shows a plain
// #statusContainer line, "Finding path between A and B", for as long as the
// request runs, then "Path found!" (5 s) or "Path finding failed: ...". None of
// them carry a class, so this module recognises them by text and restyles them
// in place:
//   working  a path-search loader (explorers wander from the green source dot
//            and trail toward the pink target; the first to arrive lights its
//            route, then the search restarts), "Tracing path", elapsed seconds
//   done     a green check line
//   error    the error text, kept verbatim, in the error style
// While working, body gets .nge-fp-busy so the tool bar can react (Submit
// pulses, the hint says it is tracing). Nothing of neuroglancer's is changed:
// if it rewrites the line (e.g. working -> error) the rescan re-classifies it.

const UI_ATTR = 'data-nge-fp-ui';
type Kind = 'working' | 'done' | 'error' | '';

export interface Live {
  raf: number; timer: number;
  /** End the search on its best moment: the nearest explorer's route reaches
   *  the target and lights up, then `done` is called and the loader stops. */
  finish?: (done: () => void) => void;
}
const live = new Map<HTMLElement, Live>();

/** The line's own text, ignoring anything this module injected. */
function ownText(li: HTMLElement): string {
  let s = '';
  li.childNodes.forEach(n => {
    if (n instanceof HTMLElement && n.hasAttribute(UI_ATTR)) return;
    s += n.textContent ?? '';
  });
  return s.trim();
}

function kindOf(text: string): Kind {
  if (text.startsWith('Finding path between')) return 'working';
  if (text.startsWith('Path found')) return 'done';
  if (text.startsWith('Path finding failed')) return 'error';
  return '';
}

function precisionOn(): boolean {
  const cb = document.querySelector<HTMLInputElement>('.graphene-find-path label input[type="checkbox"]');
  return !!cb?.checked;
}

function stop(li: HTMLElement) {
  const l = live.get(li);
  if (l) { cancelAnimationFrame(l.raf); clearInterval(l.timer); live.delete(li); }
  li.querySelectorAll(`[${UI_ATTR}]`).forEach(e => e.remove());
}

/** Path-search loader: explorers on a biased random walk from S to T.
 *  Exported for Highlight mode's panel, which traces paths the same way. */
export function startLoader(cv: HTMLCanvasElement, l: Live, W = 190, H = 24) {
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  cv.width = W * dpr; cv.height = H * dpr;
  cv.style.width = `${W}px`; cv.style.height = `${H}px`;
  const ctx = cv.getContext('2d');
  if (!ctx) return;
  ctx.scale(dpr, dpr);
  const S = { x: 9, y: H / 2 }, T = { x: W - 9, y: H / 2 };
  type Walker = { x: number; y: number; a: number; pts: number[] };
  let walkers: Walker[] = [];
  let found: number[] | null = null, foundAt = 0;
  let ending: (() => void) | null = null;
  const reset = () => {
    walkers = Array.from({ length: 7 }, () => ({ x: S.x, y: S.y, a: (Math.random() - 0.5) * 2.4, pts: [S.x, S.y] }));
    found = null;
  };
  reset();
  const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  const dot = (x: number, y: number, c: string, r: number) => {
    ctx.fillStyle = c; ctx.shadowColor = c; ctx.shadowBlur = 6;
    ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill(); ctx.shadowBlur = 0;
  };
  const frame = (now: number) => {
    // Fade old trails rather than clearing, so paths linger then dissolve.
    ctx.globalCompositeOperation = 'destination-out';
    // During the finish the jittered ghosts must die fast, or they pile into a blob.
    ctx.fillStyle = `rgba(0,0,0,${ending ? 0.26 : found ? 0.04 : 0.09})`;
    ctx.fillRect(0, 0, W, H);
    ctx.globalCompositeOperation = 'source-over';
    if (!found && !reduce) {
      for (const w of walkers) {
        const toT = Math.atan2(T.y - w.y, T.x - w.x);
        // Steer toward the target with jitter, bounce off the top and bottom.
        let d = toT - w.a; d = Math.atan2(Math.sin(d), Math.cos(d));
        w.a += d * 0.08 + (Math.random() - 0.5) * 0.9;
        const px = w.x, py = w.y;
        w.x += Math.cos(w.a) * 1.25; w.y += Math.sin(w.a) * 1.25;
        if (w.y < 2 || w.y > H - 2) { w.a = -w.a; w.y = Math.max(2, Math.min(H - 2, w.y)); }
        if (w.x < 2) { w.a = Math.PI - w.a; w.x = 2; }
        w.pts.push(w.x, w.y);
        ctx.strokeStyle = 'rgba(200,164,255,0.55)'; ctx.lineWidth = 1;
        ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(w.x, w.y); ctx.stroke();
        if (Math.hypot(T.x - w.x, T.y - w.y) < 5) { found = w.pts; foundAt = now; break; }
      }
      // A search that wandered too long starts over.
      if (!found && walkers[0].pts.length > 900) reset();
    }
    if (found && ending) {
      // The finish (Ames 2026-10-02: "a little extra voltage", a clear success
      // state). The route strikes, then surges: it runs hot and flickers,
      // throws sparks, turns from violet to success green, and the target
      // answers with a ring. Then `done`.
      const t = now - foundAt;
      const STRIKE = 260, SURGE = 1150, END = 1500;
      const k = Math.min(1, t / STRIKE);
      const n = Math.max(2, Math.floor((found.length / 2) * k));
      const g = Math.max(0, Math.min(1, (t - STRIKE) / 450));           // violet -> green
      const fade = t > SURGE ? 1 - (t - SURGE) / (END - SURGE) : 1;
      const rgb = `${Math.round(235 - 111 * g)},${Math.round(220 + 35 * g)},${Math.round(255 - 77 * g)}`;
      const stroke = (jitter: number, width: number, alpha: number, blur: number) => {
        ctx.strokeStyle = `rgba(${rgb},${alpha * fade})`;
        ctx.lineWidth = width; ctx.shadowColor = `rgb(${rgb})`; ctx.shadowBlur = blur;
        ctx.beginPath();
        for (let i = 0; i < n; i++) {
          const x = found![i * 2] + (jitter ? (Math.random() - 0.5) * jitter : 0);
          const y = found![i * 2 + 1] + (jitter ? (Math.random() - 0.5) * jitter : 0);
          i ? ctx.lineTo(x, y) : ctx.moveTo(x, y);
        }
        ctx.stroke(); ctx.shadowBlur = 0;
      };
      stroke(0, 1.8, 0.95, 10);
      if (t > STRIKE && t < SURGE) {
        // Voltage: jittered ghosts of the route, and sparks off it.
        const flick = 0.35 + Math.random() * 0.5;
        stroke(3.2, 1, flick, 6);
        stroke(5, 0.7, flick * 0.6, 4);
        for (let s = 0; s < 2; s++) {
          const i = Math.floor(Math.random() * (found.length / 2));
          const x = found[i * 2], y = found[i * 2 + 1], a = Math.random() * Math.PI * 2, len = 3 + Math.random() * 6;
          ctx.strokeStyle = `rgba(255,255,255,${0.75 * fade})`; ctx.lineWidth = 0.8;
          ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len); ctx.stroke();
        }
      }
      if (t > STRIKE) {
        // The target answers: a ring opens out from it.
        const r = Math.min(1, (t - STRIKE) / 520);
        ctx.strokeStyle = `rgba(124,255,178,${(1 - r) * 0.9})`; ctx.lineWidth = 1.4;
        ctx.beginPath(); ctx.arc(T.x, T.y, 3 + r * 10, 0, Math.PI * 2); ctx.stroke();
      }
      const lit = t > STRIKE ? '#7cffb2' : '#ff8fcf';
      dot(S.x, S.y, '#7cffb2', 3);
      dot(T.x, T.y, lit, t > STRIKE ? 3.6 : 3);
      if (t > END) { const done = ending; ending = null; ctx.clearRect(0, 0, W, H); done(); return; }
      l.raf = requestAnimationFrame(frame);
      return;
    }
    if (found) {
      // The winning route lights up, then the search restarts.
      const k = Math.min(1, (now - foundAt) / 350);
      ctx.strokeStyle = `rgba(235,220,255,${0.9 * (1 - Math.max(0, (now - foundAt - 500) / 400))})`;
      ctx.lineWidth = 1.6; ctx.shadowColor = '#c8a4ff'; ctx.shadowBlur = 8;
      ctx.beginPath();
      const n = Math.floor((found.length / 2) * k);
      for (let i = 0; i < n; i++) i ? ctx.lineTo(found[i * 2], found[i * 2 + 1]) : ctx.moveTo(found[0], found[1]);
      ctx.stroke(); ctx.shadowBlur = 0;
      if (now - foundAt > 950) {
        ctx.clearRect(0, 0, W, H);
        reset();
      }
    }
    dot(S.x, S.y, '#6fe0a0', 3);
    dot(T.x, T.y, '#ff8fcf', 3);
    l.raf = requestAnimationFrame(frame);
  };
  l.raf = requestAnimationFrame(frame);
  l.finish = (done) => {
    ending = done;
    if (reduce) { ending = null; done(); return; }
    // A route already lighting up becomes the finish, from its first frame.
    if (found) { foundAt = performance.now(); return; }
    // The explorer nearest the target wins; its route is carried the rest
    // of the way there and lit.
    let best = walkers[0];
    for (const w of walkers) if (Math.hypot(T.x - w.x, T.y - w.y) < Math.hypot(T.x - best.x, T.y - best.y)) best = w;
    const pts = best.pts.slice();
    let x = best.x, y = best.y;
    for (let i = 0; i < 400 && Math.hypot(T.x - x, T.y - y) > 3; i++) {
      const a = Math.atan2(T.y - y, T.x - x) + (Math.random() - 0.5) * 1.1;
      x += Math.cos(a) * 2.2; y = Math.max(2, Math.min(H - 2, y + Math.sin(a) * 2.2));
      pts.push(x, y);
    }
    pts.push(T.x, T.y);
    found = pts;
    foundAt = performance.now();
  };
}

function apply(li: HTMLElement, kind: Kind) {
  stop(li);
  li.classList.remove('nge-fp-status', 'nge-fp-status--working', 'nge-fp-status--done', 'nge-fp-status--error');
  li.dataset.ngeFp = kind;
  // A path Highlight mode is tracing shows its progress in the Highlight
  // box, not as a second line down here (which also lingered when the
  // server never answered). Tagged once, so it stays hidden whatever the
  // line turns into.
  if (kind === 'working' && document.body.classList.contains('nge-hl-tracing')) li.dataset.ngeHl = '1';
  if (li.dataset.ngeHl === '1') { li.style.display = 'none'; return; }
  if (!kind) return;
  li.classList.add('nge-fp-status', `nge-fp-status--${kind}`);
  if (kind === 'error') {
    // keep neuroglancer's error text as is, just styled, and let it go after
    // a while (a player: failure lines piled up and stayed, 2026-10-20)
    setTimeout(() => {
      if (!li.isConnected || li.dataset.ngeFp !== 'error') return;
      const dismiss = Array.from(li.querySelectorAll('button')).find(b => /dismiss/i.test(b.textContent || ''));
      if (dismiss) dismiss.click(); else li.remove();
    }, 12000);
    return;
  }

  const ui = document.createElement('span');
  ui.setAttribute(UI_ATTR, '');
  ui.className = 'nge-fp-ui';
  if (kind === 'done') {
    ui.innerHTML = '<span class="nge-fp-check">✓</span><span class="nge-fp-label">Path found</span>';
    li.appendChild(ui);
    // Submit stops pulsing until a point moves (Ames 2026-10-18: "Submit at
    // bottom keeps pulsing"), and anyone waiting on a path hears of it.
    foundPoints = pointsNow();
    document.body.classList.add('nge-fp-found');
    document.dispatchEvent(new CustomEvent('nge:path-found'));
    return;
  }
  const cv = document.createElement('canvas');
  cv.className = 'nge-fp-loader';
  const label = document.createElement('span');
  label.className = 'nge-fp-label';
  label.textContent = 'Tracing path';
  const secs = document.createElement('span');
  secs.className = 'nge-fp-secs';
  secs.textContent = '0s';
  const note = document.createElement('span');
  note.className = 'nge-fp-note';
  note.textContent = precisionOn()
    ? 'Precision mode is on: slower, but the path follows the cell closely'
    : 'Following the cell between your two points';
  ui.append(cv, label, secs, note);
  li.appendChild(ui);
  li.title = ownText(li);
  const l: Live = { raf: 0, timer: 0 };
  const t0 = Date.now();
  l.timer = window.setInterval(() => {
    const s = Math.round((Date.now() - t0) / 1000);
    secs.textContent = s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  }, 1000);
  live.set(li, l);
  startLoader(cv, l);
}

/** The find path points as text, to notice when one moves. */
let foundPoints = '';
function pointsNow(): string {
  return document.querySelector('.graphene-find-path .find-path-annotations')?.textContent ?? '';
}

function rescan() {
  const container = document.getElementById('statusContainer');
  let working = false;
  if (container) {
    for (const el of Array.from(container.children)) {
      const li = el as HTMLElement;
      const kind = kindOf(ownText(li));
      if (kind === 'working') working = true;
      if ((li.dataset.ngeFp ?? '') !== kind) apply(li, kind);
    }
  }
  // Lines neuroglancer has already removed: stop their animation.
  for (const li of Array.from(live.keys())) if (!li.isConnected) stop(li);
  document.body.classList.toggle('nge-fp-busy', working);
  if (document.body.classList.contains('nge-fp-found') && (working || pointsNow() !== foundPoints)) {
    document.body.classList.remove('nge-fp-found');
  }
}

let queued = false;
function watch() {
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; rescan(); });
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
}
// The bundle can run before <body> exists; never let that break startup.
if (document.body) watch();
else document.addEventListener('DOMContentLoaded', watch, { once: true });
